import { A11yModule } from '@angular/cdk/a11y'
import { TemplatePortal } from '@angular/cdk/portal'
import { CommonModule } from '@angular/common'
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core'
import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { VocabularyTerms } from '@ftr/contracts/type/core'
import { DataStore, Uuid } from '@ftr/contracts/type/shared'
import { TimeRangeTextComponent } from '@ftr/forms'
import {
  ButtonColor,
  ButtonComponent,
  ButtonDisplayType,
  DestroySubscribers,
  LoaderComponent,
  NotificationModule,
  NotificationType,
  PortalService,
  TimeRange,
  modalOnClick,
  toLocalTime,
  unwrapData,
} from '@ftr/foundation'
import { CourtRecordingService } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { Observable, filter, takeUntil } from 'rxjs'
import { PlayerService } from '../../services'
import { FetchCourtRecording } from '../../store'

export enum SealMode {
  Seal = 'seal',
  Unseal = 'unseal',
}

@Component({
  selector: 'ftr-seal-unseal-modal',
  templateUrl: './seal-unseal-modal.component.html',
  imports: [A11yModule, ButtonComponent, CommonModule, LoaderComponent, NotificationModule, TimeRangeTextComponent],
  standalone: true,
})
export class SealUnsealModalComponent extends DestroySubscribers implements OnInit {
  @Input() isModalOpen$: Observable<boolean>
  @Input() sealingTimeRange: TimeRange | undefined
  @Input() mode: SealMode
  @Input() recordingId: Uuid
  @Input() courtSystemId: Uuid
  @Input() terms: VocabularyTerms

  @ViewChild('modal') modal: TemplateRef<never>

  @Output() onSuccess = new EventEmitter<boolean>()

  readonly notificationType = NotificationType
  readonly modes = SealMode
  readonly buttonColor = ButtonColor
  readonly buttonDisplayType = ButtonDisplayType

  isCallingApi = false
  serverError = ''

  constructor(
    private readonly portalService: PortalService,
    private readonly store: Store,
    private readonly courtRecordingService: CourtRecordingService,
    private readonly playerService: PlayerService,
    private readonly viewContainerRef: ViewContainerRef,
  ) {
    super()
  }

  ngOnInit(): void {
    this.isModalOpen$.pipe(filter(m => Boolean(m))).subscribe(() => {
      this.portalService.setContent(new TemplatePortal(this.modal, this.viewContainerRef))
    })
  }

  closeSealingModal = (): void => {
    this.serverError = ''
    this.portalService.setContent(undefined)
  }

  confirm(): void {
    this.callApiToSealOrUnseal()
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent): void {
    modalOnClick(event, this.closeSealingModal)
  }

  getTermForMode(): string {
    switch (this.mode) {
      case SealMode.Seal:
        return this.terms.seal.singular
      case SealMode.Unseal:
        return this.terms.unseal.singular
      default:
        assertUnreachable(this.mode)
    }
  }

  private setError(message = `An error occurred while attempting to ${this.getTermForMode().toLowerCase()}.`): void {
    this.serverError = message
    this.isCallingApi = false
  }

  private callApiToSealOrUnseal(): void {
    this.isCallingApi = true
    const startSecondOfDay = toLocalTime(this.sealingTimeRange?.start!).toSecondOfDay()
    const endSecondOfDay = toLocalTime(this.sealingTimeRange?.end!).toSecondOfDay()

    this.sealUnsealSecondsOfDayRange(startSecondOfDay, endSecondOfDay)
  }

  private sealUnsealSecondsOfDayRange(startSecondOfDay: number, endSecondOfDay: number): void {
    this.courtRecordingService[this.mode](this.recordingId, startSecondOfDay, endSecondOfDay)
      .pipe(unwrapData(), takeUntil(this.finalize))
      .subscribe({
        next: () => {
          this.store.dispatch(new FetchCourtRecording(this.recordingId, this.courtSystemId, DataStore.Core, true))
          this.isCallingApi = false
          this.playerService.reloadPlaylist()
          this.closeSealingModal()
          this.onSuccess.emit(true)
        },
        error: () => {
          this.setError()
        },
      })
  }
}
